<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Reports', path: {name: 'admin-reports'} }]" name="Buyers by Date" :title="pageTitle"/>
    <admin-panel with-header>
      <b-table
        :fields="reportColumns"
        :items="reportRows"
        table-class="text-nowrap"
        responsive
        hover
      >
        <template #cell(main_column)="data">
          <template v-if="typeof data.item.company_id !== 'undefined'">
            <b-link
              @click.prevent="() => {filters.company = data.item.company_id; loadReportData();}"
            >
              {{ data.item.main_column }}
            </b-link>
          </template>
          <template v-else>{{ data.item.main_column }}</template>

          <template v-if="typeof data.item.is_total !== 'undefined'">
            <b-link
              v-if="company"
              class="text-danger"
              @click.prevent="() => {filters.company = null; loadReportData();}"
            >
              clear
            </b-link>
          </template>
        </template>
      </b-table>
      <template v-slot:header>
        <div class="d-flex justify-content-between">
          <div class="d-flex">
            <div class="mr-3">
              <strong class="text-uppercase">Start Date:</strong> {{ startDate }}
              <b-form-datepicker
                v-model="filters.start_date"
                class="ml-2"
                :max="maxDate"
                button-only
                button-variant="primary"
                @input="onStartDateChanged"
              />
            </div>
            <div>
              <strong class="text-uppercase">End Date:</strong> {{ endDate }}
              <b-form-datepicker
                v-model="filters.end_date"
                class="ml-2"
                :max="maxDate"
                button-only
                button-variant="primary"
                @input="onEndDateChanged"
              />
            </div>
          </div>
          <div>
            <b-btn
              variant="primary"
              v-b-tooltip.hover title="Download CSV"
              @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>
            <b-btn
              variant="primary"
              v-b-tooltip.hover title="Reset Filters"
              @click.prevent="resetFilters"
            >
              <b-icon-arrow-counterclockwise/>
            </b-btn>
          </div>
        </div>
      </template>
    </admin-panel>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import moment from "moment";
import {getFormattedPrice} from "../helpers";
import {API_ROUTES} from "../api";
export default {
  name: "AdminReportBuyersByDate",
  components: {AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    let query = this.$route.query;

    return {
      loading: true,
      report_data: null,
      company: null,
      companies: {},
      filters: {
        start_date: query.start_date ?? moment().startOf('month').format('YYYY-MM-DD'),
        end_date: query.end_date ?? moment().format('YYYY-MM-DD'),
        company: query.company ?? null
      },
    }
  },
  methods: {
    formatPrice(price) {
      return getFormattedPrice(price);
    },
    setQueryVar(key, value) {
      let query = { ...this.$route.query };
      if (!value && typeof query[key] !== 'undefined') {
        delete query[key];
      } else if (value) {
        query[key] = value;
      }
      this.$router.push({query});
    },
    onStartDateChanged(startDate) {
      if (moment(startDate).isAfter(moment(this.filters.end_date))) {
        this.filters.end_date = startDate;
      }
      this.loadReportData();
    },
    onEndDateChanged(endDate) {
      if (moment(endDate).isBefore(moment(this.filters.start_date))) {
        this.filters.start_date = endDate;
      }
      this.loadReportData();
    },
    loadReportData() {
      let reportUrl = API_ROUTES.ADMIN_REPORT_BUYERS_BY_DATE,
        params = {
          start_date: this.filters.start_date,
          end_date: this.filters.end_date
        }
      if (this.filters.company) {
        reportUrl += `/${this.filters.company}`;
      }
      this.loading = true;
      this.$api.get(reportUrl, {params}).then(({data}) => {
        this.report_data = data.report_data;
        this.company = data.company;
        this.companies = data.companies;
      }).catch(({response}) => {
        let message = 'Failed to load report';
        if (response.status === 422) {
          message = 'Some of your params were incorrect';
          this.resetFilters();
        }
        this.$bvToast.toast(message, {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      })
    },
    resetFilters() {
      this.filters = {
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        category_type: null,
        category: null
      };

      this.loadReportData();
    },
    downloadCsv() {
      if (this.reportRows.length > 2) {
        let csvContent = "data:text/csv;charset=utf-8,",
          mainColName = 'Company';
        if (this.company) {
          mainColName = 'Date';
        }
        let headers = [
          mainColName,
          'Purchases',
          'Win Price',
          'Logistic Fee',
          'Seller Fee',
          'Buyer Fee',
          'Total Fee',
          'Seller Fee Paid',
          'Buyer Fee Paid',
          'Total Fee Paid'
        ];
        csvContent += headers.join(",") + "\r\n";
        for (let i = 1; i < this.reportRows.length - 1; i++) { //remove total columns.
          let row = this.reportRows[i];
          let rowData = [
            row.main_column,
            row.sales,
            row.win_price,
            row.shipping_price,
            row.seller_fee,
            row.buyer_fee,
            row.total_fee,
            row.seller_fee_paid,
            row.buyer_fee_paid,
            row.total_fee_paid
          ];
          csvContent += rowData.join(",") + "\r\n";
        }
        let encodedUri = encodeURI(csvContent),
          link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "gl_buyers_by_date.csv");
        document.body.appendChild(link);
        link.click();
      }
    },
  },
  computed: {
    pageTitle() {
      let title = 'Buyers by Date';
      if (this.company) {
        title += `: ${this.company}`;
      }

      return title;
    },
    maxDate() {
      return moment().format('YYYY-MM-DD');
    },
    startDate() {
      return moment(this.filters.start_date).format('L');
    },
    endDate() {
      return moment(this.filters.end_date).format('L');
    },
    reportColumns() {
      let mainColName = 'Company';
      if (this.company) {
        mainColName = 'Date';
      }
      return [
        {
          key: "main_column",
          label: mainColName,
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "sales",
          label: "Purchases",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "win_price",
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "shipping_price",
          label: 'Logistic Fee',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "seller_fee",
          label: 'Seller Fee',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "buyer_fee",
          label: 'Buyer Fee',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "total_fee",
          label: 'Total Fee',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "seller_fee_paid",
          label: 'Seller Fee Paid',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "buyer_fee_paid",
          label: 'Buyer Fee Paid',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "total_fee_paid",
          label: 'Total Fee Paid',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        }
      ]
    },
    reportRows() {
      if (!this.report_data) {
        return [];
      }

      let totalColumn = 'TOTAL';
      if (this.company) {
        totalColumn = `TOTAL: ${this.company}`;
      }

      let totalRow = {
        is_total: true,
        main_column: totalColumn,
        sales: this.report_data.total.sales,
        win_price: this.report_data.total.win_price,
        shipping_price: this.report_data.total.shipping_price,
        seller_fee: this.report_data.total.seller_fee,
        buyer_fee: this.report_data.total.buyer_fee,
        total_fee: this.report_data.total.total_fee,
        seller_fee_paid: this.report_data.total.seller_fee_paid,
        buyer_fee_paid: this.report_data.total.buyer_fee_paid,
        total_fee_paid: this.report_data.total.total_fee_paid,
        _rowVariant: 'success'
      };
      let rows = [totalRow];

      if (this.company) {
        for (let date in this.report_data.dates) {
          rows.push({
            main_column: moment(date).format('L'),
            sales: this.report_data.dates[date].sales,
            win_price: this.report_data.dates[date].win_price,
            shipping_price: this.report_data.dates[date].shipping_price,
            seller_fee: this.report_data.dates[date].seller_fee,
            buyer_fee: this.report_data.dates[date].buyer_fee,
            total_fee: this.report_data.dates[date].total_fee,
            seller_fee_paid: this.report_data.dates[date].seller_fee_paid,
            buyer_fee_paid: this.report_data.dates[date].buyer_fee_paid,
            total_fee_paid: this.report_data.dates[date].total_fee_paid
          })
        }
      } else {
        for (let companyId in this.report_data.companies) {
          rows.push({
            company_id: companyId,
            main_column: this.companies[companyId],
            sales: this.report_data.companies[companyId].sales,
            win_price: this.report_data.companies[companyId].win_price,
            shipping_price: this.report_data.companies[companyId].shipping_price,
            seller_fee: this.report_data.companies[companyId].seller_fee,
            buyer_fee: this.report_data.companies[companyId].buyer_fee,
            total_fee: this.report_data.companies[companyId].total_fee,
            seller_fee_paid: this.report_data.companies[companyId].seller_fee_paid,
            buyer_fee_paid: this.report_data.companies[companyId].buyer_fee_paid,
            total_fee_paid: this.report_data.companies[companyId].total_fee_paid
          })
        }
      }

      rows.push(totalRow);

      return rows;
    }
  },
  watch: {
    "filters.company": function (categoryId) {
      this.setQueryVar('company', categoryId);
    },
    "filters.start_date": function (startDate) {
      if (startDate === moment().startOf('month').format('YYYY-MM-DD')) {
        startDate = null;
      }
      this.setQueryVar('start_date', startDate);
    },
    "filters.end_date": function (endDate) {
      if (endDate === moment().format('YYYY-MM-DD')) {
        endDate = null;
      }
      this.setQueryVar('end_date', endDate);
    },
  },
  created() {
    this.loadReportData();
  }
}
</script>